/**
 * MediaBubbleFrame — shared layout shell for media bubbles (`ImageBubble`,
 * `VideoBubble`). Co-located in `bubbles/` per Task 27's "extract if >50%
 * shared" directive.
 *
 * Flutter source of truth:
 *  - Image variants: `currentUserMessageTypeImage` (`chat_screen.dart:1566-1661`)
 *    / `peerUserMessageTypeImage` (`chat_screen.dart:2667-2757`).
 *  - Video variants: `currentUserMessageTypeVideo` (`chat_screen.dart:1663-1773`)
 *    / `peerUserMessageTypeVideo` (`chat_screen.dart:2764-2874`).
 *  - Sizing rule (shared between image / video / shareContent):
 *    `chat_screen.dart:1297-1311` —
 *        `imageWidth = aspectRatio > 1 ? width * 0.75 : width / 2`
 *        `imageHeight = imageWidth / aspectRatio`
 *
 * Both Flutter image/video variants are structurally identical — differences:
 *  - Side alignment (own right / peer left) is the dispatcher's job (handled
 *    by the outer row in `MessageBubble`); this frame just renders the media
 *    surface itself.
 *  - Padding gated on `isOwn`: own → `EdgeInsets.only(right: 4, top: 12)`
 *    (`chat_screen.dart:1612-1613`, `:1708-1709`); peer →
 *    `EdgeInsets.only(left: 4, top: 12)` (`chat_screen.dart:2725-2726`,
 *    `:2820-2821`).
 *  - Video variant overlays a centered `Icons.play_arrow` (40px, white at
 *    70% alpha) on top of the same thumbnail — that is `children` here.
 *
 * Aspect-ratio rule (Flutter parity):
 *  - `aspectRatio > 1` (landscape) → bubble width budget = `75vw` (Flutter
 *    `width * 0.75`).
 *  - `aspectRatio <= 1` (portrait / square) → bubble width budget = `50vw`
 *    (Flutter `width / 2`).
 *  - Height derives from CSS `aspect-ratio` (= width / height). Flutter
 *    computes `imageHeight = imageWidth / aspectRatio`; CSS `aspect-ratio`
 *    is `width / height`, so the value we hand the browser is the
 *    `aspectRatio` itself — the layout falls out automatically without
 *    JS sizing.
 *  - Height cap (`50vh`) so a tall thumbnail never overflows the visible
 *    chat area between the sticky app bar and the bottom composer. This is
 *    NOT applied as a `max-height` (which would compete with `aspect-ratio`
 *    and break the ratio — `aspect-ratio` only sizes the *unconstrained*
 *    dimension, so a binding `max-height` shrinks the height while the
 *    explicit width stays put, leaving the box a different ratio than the
 *    media and the inner `object-fit: cover` cropping it). Instead the cap
 *    is folded into the width: the rendered width is
 *    `min(widthBudget, 50vh * aspectRatio)`, so width stays the single
 *    explicit dimension, the `aspect-ratio`-derived height can never exceed
 *    `50vh`, and the media's real ratio is preserved in every orientation.
 *  - When `aspectRatio` is undefined (defensive — should not happen for
 *    media messages, but the field is optional on the DTO), default to
 *    `1` (square) and use the `50vw` branch. This mirrors Flutter's
 *    behavior where `aspectRatio!` would assert and crash; the web fallback
 *    is cosmetic-only.
 *
 * `vw` units mirror what `UnreadBanner` already does
 * (`UnreadBanner.tsx:175`) for chat-width-relative sizing. Flutter's
 * `MediaQuery.of(context).size.width` is the viewport width on mobile; on
 * the web the chat page's content width is roughly the viewport width
 * (the centered desktop layout is wider than the bubble would ever need),
 * so `vw` is a faithful 1:1 mapping.
 *
 * Theme tokens:
 *  - Placeholder background while the image is loading / the message is
 *    processing → `theme.secondary` — the project's standard image-loading /
 *    skeleton placeholder background (matches what `CachedImage` uses for its
 *    own default placeholder). This intentionally diverges from a literal
 *    match of Flutter's `AppColors.backgroundColorLight` (the fixed off-white
 *    used in Flutter's uploading branch, `chat_screen.dart:1622-1626`,
 *    `:1719-1721`): the prior `theme.whiteLight` token was the same fixed
 *    off-white in both web themes, which rendered as a jarring white block in
 *    dark mode. `theme.secondary` is mode-aware (dark neutral in dark mode,
 *    light neutral in light mode), so the processing surface reads as a
 *    neutral filler in both modes.
 *
 * Responsive tokens:
 *  - Border radius → `responsive.borderRadius.base` (= 8) — exact parity
 *    with Flutter's `BorderRadius.all(Radius.circular(8.0))`
 *    (`chat_screen.dart:1615`, `:1711`, `:2731`, `:2826`). The asymmetric
 *    corner-radii rule from `getBubbleCornerRadii` overrides this for the
 *    actual corner values; the `8` is only the rounded clipping fallback
 *    when both `topLeft`/`bottomRight` collapse to mid.
 *  - Side margin (own right / peer left) → `responsive.spacing.xs`
 *    (= 4) — exact parity with Flutter's `EdgeInsets.only(right: 4)` /
 *    `(left: 4)`.
 *  - Top margin → `responsive.spacing.base` (= 12) — exact parity with
 *    Flutter's `EdgeInsets.only(top: 12)`.
 *
 * Uploading state: Flutter shows a placeholder rectangle with a centered,
 * italic "Processing image" / "Processing video" label while the upload is
 * in flight (`chat_screen.dart:1619-1636` image, `:1714-1734` video). We
 * render the same label from `l10n.chat.uploading.image` / `.video` — the
 * caller (`ImageBubble` / `VideoBubble`) resolves the correct string and
 * passes it as `processingLabel`, since the frame itself does not know the
 * message type. The label is colored `theme.text.primary` (mode-aware:
 * near-white in dark mode, near-black in light mode) so it reads on the
 * `theme.secondary` placeholder surface in both themes; Flutter's literal
 * `AppColors.white` would be invisible on the light-mode placeholder. The
 * label is gated on `isUploading` — the non-uploading "thumbnail still
 * loading" placeholder (server doc present, image not yet fetched) shows a
 * bare surface, matching Flutter.
 */

import React from 'react';
import type { CSSProperties } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { EncryptedCachedImage } from '@/presentation/components/common/EncryptedCachedImage';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';
import { getRatioPreservingMediaWidthCss } from '@/presentation/utils/mediaSizing';

export interface MediaBubbleFrameProps {
  /** Whether the message is owned by the current user. */
  isOwn: boolean;
  /** Whether this bubble is the run-end (see Flutter `isLastMessageRight` / `Left`). */
  isLastOfRun: boolean;
  /** Media aspect-ratio (`width / height`). Defaults to `1` (square) when missing. */
  aspectRatio: number | undefined;
  /**
   * Thumbnail source. Empty string → render placeholder. Its meaning depends on
   * the render path selected by `peerPublicJwk`:
   *  - ENCRYPTED path (`peerPublicJwk` supplied — chat direct image/video
   *    bubbles `ImageBubble` / `VideoBubble`): a BUCKET-RELATIVE object path
   *    (e.g. `chat/{group}/thumbnail/{ts}`), NOT a ready URL. The bytes are E2EE
   *    ciphertext produced by `encryptBinary` (Task 9/10/11);
   *    `EncryptedCachedImage` resolves a valid, tokenized download URL from this
   *    relative path via the domain UseCase, then fetches, decrypts, and renders
   *    a `blob:` (Finding 5 — prefixing `DEFAULT_BUCKET_PREFIX` here produced an
   *    un-encoded, token-less URL the Storage REST endpoint rejected with HTTP
   *    400).
   *  - PLAINTEXT path (`peerPublicJwk` omitted — shared exclusive-content
   *    thumbnails `ShareContentBubble`): an ALREADY-RESOLVED content-CDN URL.
   *    The bytes are a plaintext image (subscription-gated at the CDN, not
   *    chat-E2EE-encrypted) and render directly via `CachedImage`.
   */
  thumbnailSource: string;
  /**
   * Peer's ECDH public JWK (from the chat's `peerUser.publicJwk`). Presence is
   * the discriminator that selects the ENCRYPTED render path: when supplied,
   * the thumbnail is decrypted by `EncryptedCachedImage` using this peer key;
   * when omitted, the thumbnail is treated as plaintext and rendered by
   * `CachedImage` (see `thumbnailSource`). A null value still selects the
   * encrypted path but fails to derive a key, so the ciphertext fails to render
   * (error placeholder), matching the legacy-media contract.
   */
  peerPublicJwk?: JsonWebKey | null;
  /** True when the message is mid-upload (renders the placeholder surface). */
  isUploading: boolean;
  /**
   * Label shown centered over the placeholder while uploading (e.g.
   * "Processing image" / "Processing video"). Resolved from
   * `l10n.chat.uploading.image` / `.video` by the caller, since the frame
   * does not know whether the message is an image or a video.
   */
  processingLabel: string;
  /** Tap handler — fired by the wrapping button. */
  onTap: () => void;
  /**
   * `data-qa-*` set spread onto the wrapping `<button>` (the primary tappable
   * media content) so the qa-tester can open the media. The caller builds the
   * id as `chat-bubble-<type>-<messageId>`.
   */
  qa?: QaAttrOptions;
  /**
   * Optional overlay (e.g. video play icon). Rendered absolutely-positioned
   * on top of the thumbnail. The frame applies `pointerEvents: none` to the
   * overlay container so the tap always lands on the underlying button.
   */
  children?: React.ReactNode;
}

export const MediaBubbleFrame: React.FC<MediaBubbleFrameProps> = ({
  isOwn,
  isLastOfRun,
  aspectRatio,
  thumbnailSource,
  peerPublicJwk,
  isUploading,
  processingLabel,
  onTap,
  qa,
  children,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Asymmetric corners — same rule as every other bubble. The media surface
  // is clipped to the bubble corners via `overflow: hidden` + the corner
  // radii below; the inner thumbnail does not need to know its own corners.
  const corners = ChatUtils.getBubbleCornerRadii({
    isOwn,
    isLastOfRun,
    runMidRadius: responsive.borderRadius.lg,
    runEndRadius: responsive.borderRadius.sm,
  });

  // Dual-cap, ratio-preserving width (Flutter `chat_screen.dart:1305-1311`).
  // Width is the single explicit dimension, clamped so the `aspect-ratio`-derived
  // height can never exceed `50vh`; the full rationale (why the height cap is
  // folded into the width instead of `max-height`) lives on the shared helper.
  const resolvedAspectRatio = aspectRatio ?? 1;
  const bubbleWidthCss = getRatioPreservingMediaWidthCss(resolvedAspectRatio);

  // Placeholder surface color while the thumbnail loads or the message is
  // mid-upload. `theme.secondary` is the project's standard image-loading /
  // skeleton placeholder background (it is what `CachedImage` uses for its
  // own default placeholder, `CachedImage.tsx:276`), and unlike the previous
  // `theme.whiteLight` it is mode-aware — a dark neutral in dark mode and a
  // light neutral in light mode — so the processing surface no longer reads
  // as a jarring white block against the dark chat background.
  const placeholderColor = theme.secondary;

  const buttonStyle: CSSProperties = {
    // Reset native button chrome.
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    // One-sided side margin gated on `isOwn` (Flutter
    // `EdgeInsets.only(right: 4)` for own / `(left: 4)` for peer at
    // `chat_screen.dart:1613` / `:1709` / `:2726` / `:2821`).
    marginRight: isOwn ? responsive.spacing.xs : 0,
    marginLeft: !isOwn ? responsive.spacing.xs : 0,
    // Top margin — Flutter `EdgeInsets.only(top: 12)`.
    marginTop: responsive.spacing.base,
    // Ratio-preserving width capped on BOTH axes (see `bubbleWidthCss`
    // above): width is the only explicit dimension, already clamped so the
    // `aspect-ratio`-derived height can never exceed `50vh`. Height is left
    // to fall out of `aspect-ratio` so the media's real ratio always holds
    // and `object-fit: cover` never crops — no explicit/`max-height` is set
    // because any height constraint would compete with `aspect-ratio` and
    // re-break the ratio.
    width: bubbleWidthCss,
    aspectRatio: String(resolvedAspectRatio),
    // Bubble corners — same asymmetric rule as the other bubbles. The
    // inner image fills the rounded clipped box via `overflow: hidden`.
    borderTopLeftRadius: corners.topLeft,
    borderTopRightRadius: corners.topRight,
    borderBottomLeftRadius: corners.bottomLeft,
    borderBottomRightRadius: corners.bottomRight,
    overflow: 'hidden',
    backgroundColor: placeholderColor,
    display: 'block',
    // Required so the absolutely-positioned overlay (video play icon)
    // anchors to this frame.
    position: 'relative',
  };

  // Shared inner-image style for both render paths (encrypted / plaintext).
  // The image fills the rounded, clipped frame; `object-fit: cover` crops to
  // the bubble box (the box already preserves the media's real aspect ratio,
  // so `cover` never distorts — see the `bubbleWidthCss` note above).
  const imageStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  };

  // Coarse media load token for the qa-tester (`data-qa-status`). Reuses the
  // already-computed `isUploading` / `thumbnailSource` placeholder branch — no
  // new state. Only emitted when the caller passes a `qa` id.
  const mediaQa = qa
    ? qaAttr({
        ...qa,
        status:
          qa.status ??
          (isUploading ? 'uploading' : thumbnailSource ? 'ready' : 'loading'),
      })
    : {};

  return (
    <button type="button" {...mediaQa} onClick={onTap} style={buttonStyle}>
      {isUploading || !thumbnailSource ? (
        // Placeholder rectangle. While the message is mid-upload it carries
        // a centered, italic "Processing image" / "Processing video" label
        // (Flutter `chat_screen.dart:1619-1636`, `:1714-1734`); the
        // non-uploading "thumbnail still loading" case shows a bare surface,
        // matching Flutter (the label is gated on `isUploading`).
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: placeholderColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isUploading && (
            <span
              style={{
                // `theme.text.primary` is mode-aware (near-white in dark mode,
                // near-black in light mode) so the label is readable on the
                // `theme.secondary` placeholder surface in both modes.
                color: theme.text.primary,
                fontStyle: 'italic',
                // `font.sm` = 12px on mobile — exact parity with Flutter's
                // `fontSize: 12.0` (same token `MessageTimestamp` uses for the
                // identical 12px chat caption).
                fontSize: responsive.font.sm,
                textAlign: 'center',
              }}
            >
              {processingLabel}
            </span>
          )}
        </div>
      ) : peerPublicJwk !== undefined ? (
        // Chat direct image/video bubble — the thumbnail bytes are E2EE
        // ciphertext, not a plaintext image, so we use `EncryptedCachedImage`
        // (resolve relative path → tokenized download URL → fetch → decrypt →
        // `blob:`) rather than `CachedImage` (direct-URL). `thumbnailSource` is
        // the BUCKET-RELATIVE object path here; `EncryptedCachedImage` resolves
        // the valid download URL itself via the domain UseCase (Finding 5). The
        // peer key drives the decrypt (a `null` key fails to derive and the
        // ciphertext surfaces the error placeholder — mode-aware
        // `theme.secondary` surface — instead of a broken image; story
        // Assumption A / consideration A/E).
        <EncryptedCachedImage
          src={thumbnailSource}
          peerPublicJwk={peerPublicJwk}
          alt=""
          style={imageStyle}
          // Decrypt-lifecycle `data-qa-status` (`loading` | `ready` | `error`)
          // on the inline tile's inner image, so a UI-test can `wait_for` the
          // fetch→decrypt state on the bubble itself — not just the wrapping
          // button's coarse `mediaQa` (which is derived from `thumbnailSource`
          // presence, not the decrypt lifecycle). Derived from the same
          // per-message `qa.id` the button uses, suffixed `-thumb` so the two
          // tokens never collide; omitted when the caller passes no `qa` id.
          qaId={qa?.id ? `${qa.id}-thumb` : undefined}
        />
      ) : (
        // Shared exclusive-content thumbnail (`ShareContentBubble`) — a
        // PLAINTEXT content-CDN image (subscription-gated at the CDN, NOT
        // chat-E2EE-encrypted), so it renders directly via `CachedImage`.
        // `thumbnailSource` is an already-resolved CDN URL here (the plaintext
        // branch is unchanged by Finding 5). The omitted `peerPublicJwk` is the
        // discriminator (see prop JSDoc).
        <CachedImage src={thumbnailSource} alt="" style={imageStyle} />
      )}
      {children !== undefined && children !== null && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // The frame's button receives the tap; overlay must not block it.
            pointerEvents: 'none',
          }}
        >
          {children}
        </div>
      )}
    </button>
  );
};
